import type { MessageKey } from '@dijitalapp/i18n';
import { REPORT_REASONS, type ReportReason } from '@dijitalapp/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

export type ReportTarget = { type: 'message'; messageId: string } | { type: 'user'; userId: string };

/**
 * Report flow. Evidence is opt-in and limited to messages the reporter can already see; nothing else
 * from the conversation leaves the device's view (REALTIME/E2EE moderation boundary, THREAT_MODEL).
 */
export function ReportDialog({
  target,
  recentMessageIds,
  onClose,
}: {
  target: ReportTarget;
  /** Messages offered as optional evidence (most recent last). */
  recentMessageIds: string[];
  onClose: () => void;
}) {
  const { i18n } = useApp();
  const { t } = i18n;
  const ref = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [includeRecent, setIncludeRecent] = useState(recentMessageIds.length > 0);
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [state, setState] = useState<{ status: 'idle' | 'sending' | 'sent' | 'error'; message?: string }>({ status: 'idle' });

  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    setState({ status: 'sending' });
    try {
      await api('POST', '/reports', {
        targetType: target.type,
        ...(target.type === 'message' ? { messageId: target.messageId } : { userId: target.userId }),
        reason,
        ...(details.trim() ? { details: details.trim() } : {}),
        evidenceMessageIds: includeRecent ? recentMessageIds : [],
        alsoBlock,
      });
      setState({ status: 'sent' });
    } catch (err) {
      setState({ status: 'error', message: t(errorMessageKey(err)) });
    }
  };

  const urgent = reason === 'child_safety' || reason === 'self_harm' || reason === 'violence';

  return (
    <dialog ref={ref} className="dialog" aria-labelledby="report-title" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <h2 id="report-title">{target.type === 'user' ? t('report.user') : t('report.title')}</h2>
        {state.status === 'sent' ? (
          <p role="status">{t('report.sent')}</p>
        ) : (
          <>
            <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 'var(--space-2)' }}>
              <legend className="field-legend">{t('report.reason')}</legend>
              {REPORT_REASONS.map((r) => (
                <label key={r} className="check">
                  <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
                  <span>{t(`report.reason.${r}` as MessageKey)}</span>
                </label>
              ))}
            </fieldset>
            {urgent && (
              <p className="alert alert-error" role="alert">
                {t('report.emergency')}
              </p>
            )}
            <div className="field">
              <label htmlFor="report-details">{t('report.details')}</label>
              <textarea
                id="report-details"
                className="input"
                rows={3}
                maxLength={1000}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                style={{ paddingBlock: 'var(--space-2)' }}
              />
            </div>
            {recentMessageIds.length > 0 && (
              <label className="check">
                <input type="checkbox" checked={includeRecent} onChange={(e) => setIncludeRecent(e.target.checked)} />
                <span>{t('report.includeRecent', { count: recentMessageIds.length })}</span>
              </label>
            )}
            <p className="hint">{t('report.evidenceNote')}</p>
            <label className="check">
              <input type="checkbox" checked={alsoBlock} onChange={(e) => setAlsoBlock(e.target.checked)} />
              <span>{t('report.alsoBlock')}</span>
            </label>
            {state.status === 'error' && (
              <p className="alert alert-error" role="alert">
                {state.message}
              </p>
            )}
          </>
        )}
        <div className="row">
          <span className="spacer" />
          <button className="btn" type="button" onClick={() => ref.current?.close()}>
            {state.status === 'sent' ? t('common.close') : t('common.cancel')}
          </button>
          {state.status !== 'sent' && (
            <button className="btn btn-danger" disabled={!reason || state.status === 'sending'}>
              {t('report.submit')}
            </button>
          )}
        </div>
      </form>
    </dialog>
  );
}
